import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { PiArrowCounterClockwise } from 'react-icons/pi';

import { fetchBomList } from '#/api/momMgmt/settings/bom/fetchList';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { Checkbox } from '#/modules/commonComponents/checkbox';
import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import {
  dialogContentCompactGapClassName,
  dialogWideMaxWidthClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import TableSearchInput from '#/modules/commonComponents/table/TableSearchInput';
import { createSelectColumn } from '#/modules/commonComponents/table/tableColumnHelpers';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#/modules/commonComponents/tooltip';
import { buildBomPickerColumns } from '#/modules/momMgmt/settings/bom/data/bomPickerColumns';
import useBomPickerListQuery from '#/modules/momMgmt/settings/bom/hooks/useBomPickerListQuery';
import { bomListQueryKey } from '#/utils/queryKeys';

const BOM_PICKER_SORT_FIELD_LABELS = {
  code: 'BOM代號',
  name: 'BOM名稱',
  version: '版本',
  status: '狀態',
};

function buildInitialSelection(initialSelectedId = null, rows = []) {
  if (!initialSelectedId) {
    return { rowSelection: {}, selectedRow: null };
  }

  const matched = rows.find((row) => String(row.id) === String(initialSelectedId));
  return {
    rowSelection: { [String(initialSelectedId)]: true },
    selectedRow: matched || { id: initialSelectedId },
  };
}

/**
 * 單選 ACTIVE BOM。確認回傳選中列（或 null）。
 * allowInactive：標頭多一個「含停用」勾選，勾了才列出 INACTIVE（預設仍只看 ACTIVE）。
 */
const BomPickerDialog = ({
  open,
  onOpenChange,
  initialSelectedId = null,
  initialSelectedRow = null,
  onConfirm,
  title = '請選擇 BOM',
  description = '僅可選啟用中的 BOM；單選後確認。',
  allowInactive = false,
}) => {
  const [includeInactive, setIncludeInactive] = useState(false);
  const {
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetQuery,
  } = useBomPickerListQuery({ includeInactive: allowInactive && includeInactive });

  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedRow, setSelectedRow] = useState(() => (
    initialSelectedRow || (initialSelectedId ? { id: initialSelectedId } : null)
  ));
  const [rowSelection, setRowSelection] = useState(() => (
    initialSelectedId ? { [String(initialSelectedId)]: true } : {}
  ));

  const listQueryResult = useQuery({
    queryKey: bomListQueryKey(listQuery),
    queryFn: () => fetchBomList(listQuery),
    enabled: open,
    placeholderData: keepPreviousData,
  });

  const rows = useMemo(
    () => listQueryResult.data?.data ?? [],
    [listQueryResult.data?.data],
  );

  const handleFilterChange = useCallback((key, value) => {
    setFilter(key, value);
  }, [setFilter]);

  const columns = useMemo(() => ([
    createSelectColumn({ enableMultiSelection: false, showHeaderCheckbox: false }),
    ...buildBomPickerColumns({
      filters: listQuery.filters,
      onFilterChange: handleFilterChange,
    }),
  ]), [listQuery.filters, handleFilterChange]);

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns),
    [columns],
  );

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  const handleRowSelectionChange = useCallback((selection) => {
    setRowSelection(selection);
    const selectedId = Object.keys(selection).find((key) => selection[key]);
    if (!selectedId) {
      setSelectedRow(null);
      return;
    }
    const fromPage = rows.find((row) => String(row.id) === String(selectedId));
    setSelectedRow(fromPage || selectedRow || { id: selectedId });
  }, [rows, selectedRow]);

  function handleSearchSubmit() {
    setKeyword(searchKeyword);
  }

  function handleResetFilters() {
    setSearchKeyword('');
    resetQuery();
  }

  function handleConfirm() {
    onConfirm(selectedRow || null);
    onOpenChange(false);
  }

  function handleCancel() {
    onOpenChange(false);
  }

  function handleOpenChange(nextOpen) {
    if (!nextOpen) {
      resetQuery();
      setSearchKeyword('');
      const initial = buildInitialSelection(initialSelectedId, rows);
      setRowSelection(initial.rowSelection);
      setSelectedRow(initialSelectedRow || initial.selectedRow);
    }
    onOpenChange(nextOpen);
  }

  const headerSearch = (
    <div
      className={cn(
        'flex w-full max-w-md items-center gap-2',
        allowInactive ? 'sm:w-96' : 'sm:w-72',
      )}
    >
      {allowInactive ? (
        <label className="flex shrink-0 items-center gap-1.5 text-sm">
          <Checkbox
            checked={includeInactive}
            onCheckedChange={(checked) => {
              setIncludeInactive(Boolean(checked));
              setPage(1);
            }}
            aria-label="含停用 BOM"
          />
          含停用
        </label>
      ) : null}
      <TableSearchInput
        useKeyword
        keyword={searchKeyword}
        onKeywordChange={setSearchKeyword}
        onSearch={handleSearchSubmit}
        placeholder="搜尋代號、名稱、版本"
        className="min-w-0 w-full text-foreground"
      />
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="secondary"
            size="icon"
            className={cn(
              'hover:text-gray-50',
              'size-10 shrink-0',
            )}
            aria-label="清除搜尋與篩選"
            onClick={handleResetFilters}
          >
            <PiArrowCounterClockwise size={20} aria-hidden />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">清除搜尋與篩選</TooltipContent>
      </Tooltip>
    </div>
  );

  return (
    <AppDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={title}
      description={description}
      headerVariant="primary"
      headerEnd={headerSearch}
      showCloseButton={false}
      maxWidthClassName={dialogWideMaxWidthClassName}
      contentClassName={dialogContentCompactGapClassName}
      closeOnOutsideClick={false}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      confirmText="確認選取"
      cancelText="取消"
      cancelAriaLabel="取消選 BOM"
      confirmDisabled={listQueryResult.isLoading}
    >
      <ListTablePanel
        variant="plain"
        columns={columns}
        rows={rows}
        pagination={listQueryResult.data?.page ?? {
          current_page: 1,
          page_size: 10,
          total_count: 0,
          total_page: 1,
        }}
        isLoading={listQueryResult.isLoading}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        listQuery={listQuery}
        filterStatusFields={filterStatusFields}
        sortFieldLabels={BOM_PICKER_SORT_FIELD_LABELS}
        onFilterChange={setFilter}
        onKeywordClear={() => {
          setSearchKeyword('');
          setKeyword('');
        }}
        onSortRemove={removeSortAt}
        onSortingChange={handleSortingChange}
        enableMultiSelection={false}
        paginationClassName="px-0 py-2"
        initialRowSelection={rowSelection}
        onRowSelectionChange={handleRowSelectionChange}
      />
    </AppDialog>
  );
};

BomPickerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  initialSelectedId: PropTypes.string,
  initialSelectedRow: PropTypes.shape({
    id: PropTypes.string,
    code: PropTypes.string,
    name: PropTypes.string,
    version: PropTypes.string,
    status: PropTypes.string,
  }),
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  allowInactive: PropTypes.bool,
};

export default BomPickerDialog;
